import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, Link, Navigate } from "react-router-dom";
import { api, inrPlain, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import {
  Home, Users, Users2, Receipt, CreditCard, Palette, LogOut,
  ShieldCheck, TrendingUp, Sparkles, Search, Copy, Eye, EyeOff, Save,
  MessageSquare, Trash2, Mail, Phone, Calendar,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { format } from "date-fns";

export function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = usePlatform();

  if (!user) return null;
  if (user.role !== "superadmin") return <Navigate to="/" replace />;

  const nav = [
    { to: "/admin", icon: Home, label: "Overview", testid: "admin-nav-overview", exact: true },
    { to: "/admin/users", icon: Users, label: "Users", testid: "admin-nav-users" },
    { to: "/admin/groups", icon: Users2, label: "Groups", testid: "admin-nav-groups" },
    { to: "/admin/transactions", icon: Receipt, label: "Transactions", testid: "admin-nav-transactions" },
    { to: "/admin/plan", icon: CreditCard, label: "Plan & Razorpay", testid: "admin-nav-plan" },
    { to: "/admin/leads", icon: MessageSquare, label: "AI Leads", testid: "admin-nav-leads" },
    { to: "/admin/branding", icon: Palette, label: "Branding", testid: "admin-nav-branding" },
  ];

  const isActive = (p, exact) => (exact ? location.pathname === p : location.pathname.startsWith(p));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      <div className="grid lg:grid-cols-[260px_1fr] max-w-[1600px] mx-auto">
        <aside className="hidden lg:flex flex-col p-6 gap-5 border-r border-slate-200/80 sticky top-0 h-screen bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-slate-900 text-white grid place-items-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <div className="heading text-lg font-bold text-slate-900 leading-none">{settings.title}</div>
              <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-bold mt-1">Super admin</div>
            </div>
          </div>
          <nav className="flex-1 space-y-1">
            {nav.map(({ to, icon: Icon, label, testid, exact }) => {
              const active = isActive(to, exact);
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${
                    active ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={active ? 2.5 : 2} />
                  <span className="text-sm font-semibold">{label}</span>
                </Link>
              );
            })}
          </nav>
          <button
            onClick={() => navigate("/")}
            className="text-xs text-slate-500 hover:text-slate-900 text-left px-3"
            data-testid="admin-exit-link"
          >
            ← Back to app
          </button>
          <div className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200 bg-white">
            <div className="w-9 h-9 rounded-full bg-slate-900 text-white grid place-items-center text-xs font-semibold">
              {user.name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
            </div>
            <button onClick={logout} className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500 hover:text-rose-600" data-testid="admin-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </aside>
        <main className="p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-slate-500">Loading…</div>;
  const t = stats.totals;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Platform</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight">Overview</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total users" value={t.users} sub={`${t.free_users} free · ${t.pro_users} pro`} tint="emerald" testid="stat-users" />
        <StatCard label="Groups" value={t.groups} sub={`${t.expenses} expenses`} tint="blue" testid="stat-groups" />
        <StatCard label="Settlements" value={t.settlements} sub="All-time" tint="amber" testid="stat-settlements" />
        <StatCard label="Pro revenue" value={inrPlain(stats.revenue.total_inr)} sub={`${stats.revenue.paid_transactions} paid`} tint="rose" testid="stat-revenue" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-5 rounded-3xl bg-white border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Signups · last 14d</div>
            <TrendingUp className="w-4 h-4 text-slate-400" />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={stats.signup_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: "#10b981" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="p-5 rounded-3xl bg-white border border-slate-200 space-y-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Recent</div>
          <MiniRow label="New users · 24h" value={stats.recent.new_users_24h} />
          <MiniRow label="New users · 7d" value={stats.recent.new_users_7d} />
          <MiniRow label="New expenses · 7d" value={stats.recent.new_expenses_7d} />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, tint, testid }) {
  const tints = { emerald: "bg-emerald-50 text-emerald-600", blue: "bg-blue-50 text-blue-600", amber: "bg-amber-50 text-amber-600", rose: "bg-rose-50 text-rose-600" };
  return (
    <div className="p-5 rounded-2xl bg-white border border-slate-200" data-testid={testid}>
      <div className={`w-9 h-9 rounded-xl grid place-items-center ${tints[tint]}`}>
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mt-4">{label}</div>
      <div className="amount text-2xl font-bold mt-1">{value}</div>
      <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
    </div>
  );
}
function MiniRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-sm text-slate-600">{label}</div>
      <div className="amount font-bold text-slate-900">{value}</div>
    </div>
  );
}

export function AdminUsers() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/users", { params: { q } }).then((r) => setRows(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);
  const togglePro = async (u) => {
    const nextPlan = u.plan === "pro" ? "free" : "pro";
    try {
      await api.patch(`/admin/users/${u.id}`, { plan: nextPlan });
      toast.success(`Set to ${nextPlan.toUpperCase()}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="admin-users">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Platform</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight">Users</h1>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
        <Input placeholder="Search users" value={q} onChange={(e) => setQ(e.target.value)} className="h-12 pl-10 rounded-2xl bg-white" data-testid="admin-users-search" />
      </div>
      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3 pl-5">User</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Groups</th>
              <th className="text-left p-3">Joined</th>
              <th className="text-right p-3 pr-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-user-${u.id}`}>
                <td className="p-3 pl-5">
                  <div className="font-semibold">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                    u.plan === "pro" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                  }`}>{u.plan || "free"}</span>
                </td>
                <td className="p-3 text-slate-600">{u.role || "user"}</td>
                <td className="p-3 amount font-bold">{u.group_count}</td>
                <td className="p-3 text-xs text-slate-500">{u.created_at ? format(new Date(u.created_at), "PP") : "—"}</td>
                <td className="p-3 pr-5 text-right">
                  {u.role !== "superadmin" && (
                    <Button
                      onClick={() => togglePro(u)}
                      variant="outline"
                      className="rounded-full h-8 text-xs"
                      data-testid={`admin-user-toggle-${u.id}`}
                    >
                      {u.plan === "pro" ? "Downgrade" : "Upgrade"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows?.length === 0 && <div className="text-center p-6 text-sm text-slate-500">No users</div>}
      </div>
    </div>
  );
}

export function AdminGroups() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/groups").then((r) => setRows(r.data)); }, []);
  return (
    <div className="space-y-5" data-testid="admin-groups">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Platform</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight">Groups</h1>
      </div>
      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3 pl-5">Group</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-left p-3">Members</th>
              <th className="text-left p-3">Expenses</th>
              <th className="text-left p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((g) => (
              <tr key={g.id} className="border-t border-slate-100">
                <td className="p-3 pl-5 font-semibold">{g.name}</td>
                <td className="p-3 font-mono text-xs text-slate-600">/{g.slug}</td>
                <td className="p-3 amount font-bold">{g.member_ids?.length || 0}</td>
                <td className="p-3 amount font-bold">{g.expense_count}</td>
                <td className="p-3 text-xs text-slate-500">{g.created_at ? format(new Date(g.created_at), "PP") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows?.length === 0 && <div className="text-center p-6 text-sm text-slate-500">No groups</div>}
      </div>
    </div>
  );
}

export function AdminTransactions() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/transactions").then((r) => setRows(r.data)); }, []);
  return (
    <div className="space-y-5" data-testid="admin-transactions">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Payments</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight">Transactions</h1>
      </div>
      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3 pl-5">User</th>
              <th className="text-left p-3">Amount</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Order ID</th>
              <th className="text-left p-3">Payment ID</th>
              <th className="text-left p-3">Mode</th>
              <th className="text-left p-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="p-3 pl-5">
                  <div className="font-semibold">{t.user?.name || "—"}</div>
                  <div className="text-xs text-slate-500">{t.user?.email}</div>
                </td>
                <td className="p-3 amount font-bold">{inrPlain(t.amount_inr)}</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                    t.status === "paid" ? "bg-emerald-100 text-emerald-700" :
                    t.status === "created" ? "bg-slate-100 text-slate-600" :
                    "bg-rose-100 text-rose-700"
                  }`}>{t.status}</span>
                </td>
                <td className="p-3 font-mono text-xs text-slate-600 truncate max-w-[180px]">{t.order_id}</td>
                <td className="p-3 font-mono text-xs text-slate-600 truncate max-w-[180px]">{t.payment_id || "—"}</td>
                <td className="p-3 uppercase text-xs font-semibold text-slate-500">{t.razorpay_mode}</td>
                <td className="p-3 text-xs text-slate-500">{t.created_at ? format(new Date(t.created_at), "PP") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows?.length === 0 && <div className="text-center p-6 text-sm text-slate-500">No transactions yet</div>}
      </div>
    </div>
  );
}

export function AdminPlan() {
  const [p, setP] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [showLiveSecret, setShowLiveSecret] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/plan-settings").then((r) => setP(r.data)); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/plan-settings", patch);
      setP(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!p) return <div className="text-slate-500">Loading…</div>;
  const isLive = p.razorpay_mode === "live";

  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-plan">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Monetization</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight">Plan & Razorpay</h1>
        <p className="text-sm text-slate-500 mt-1">
          Configure the Pro price and Razorpay credentials. Flip TEST → LIVE when you're ready to accept real payments.
        </p>
      </div>

      <section className="rounded-3xl bg-white border border-slate-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Pro pricing</div>
            <div className="text-xs text-slate-500 mt-0.5">Applies to new upgrades. Existing Pro users keep their remaining validity.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Price (INR)</Label>
            <Input type="number" min="1" value={p.pro_price_inr} onChange={(e) => setP({ ...p, pro_price_inr: e.target.value })} className="mt-2 h-12 rounded-2xl" data-testid="admin-plan-price" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Validity (days)</Label>
            <Input type="number" min="1" value={p.pro_duration_days} onChange={(e) => setP({ ...p, pro_duration_days: e.target.value })} className="mt-2 h-12 rounded-2xl" data-testid="admin-plan-duration" />
          </div>
        </div>
        <Button disabled={busy} onClick={() => save({ pro_price_inr: p.pro_price_inr, pro_duration_days: p.pro_duration_days })} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="admin-plan-save-pricing">
          <Save className="w-4 h-4 mr-1.5" /> Save pricing
        </Button>
      </section>

      <section className="rounded-3xl bg-white border border-slate-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              Razorpay mode
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${isLive ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {isLive ? "LIVE" : "TEST"}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Toggle to accept real payments. Enter live keys before enabling.</div>
          </div>
          <Switch
            checked={isLive}
            onCheckedChange={(v) => save({ razorpay_mode: v ? "live" : "test" })}
            data-testid="admin-plan-mode-switch"
          />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <KeyRow
            label="TEST — Key ID"
            value={p.razorpay_key_id}
            onChange={(v) => setP({ ...p, razorpay_key_id: v })}
            testid="admin-plan-test-key-id"
          />
          <KeyRow
            label="TEST — Key Secret"
            value={p.razorpay_key_secret}
            onChange={(v) => setP({ ...p, razorpay_key_secret: v })}
            testid="admin-plan-test-key-secret"
            secret
            show={showSecret}
            onToggle={() => setShowSecret((s) => !s)}
          />
          <KeyRow
            label="LIVE — Key ID"
            value={p.razorpay_live_key_id}
            onChange={(v) => setP({ ...p, razorpay_live_key_id: v })}
            testid="admin-plan-live-key-id"
          />
          <KeyRow
            label="LIVE — Key Secret"
            value={p.razorpay_live_key_secret}
            onChange={(v) => setP({ ...p, razorpay_live_key_secret: v })}
            testid="admin-plan-live-key-secret"
            secret
            show={showLiveSecret}
            onToggle={() => setShowLiveSecret((s) => !s)}
          />
          <KeyRow
            label="Webhook secret (optional)"
            value={p.webhook_secret}
            onChange={(v) => setP({ ...p, webhook_secret: v })}
            testid="admin-plan-webhook-secret"
            secret
          />
        </div>
        <Button
          disabled={busy}
          onClick={() => save({
            razorpay_key_id: p.razorpay_key_id, razorpay_key_secret: p.razorpay_key_secret,
            razorpay_live_key_id: p.razorpay_live_key_id, razorpay_live_key_secret: p.razorpay_live_key_secret,
            webhook_secret: p.webhook_secret,
          })}
          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="admin-plan-save-keys"
        >
          <Save className="w-4 h-4 mr-1.5" /> Save keys
        </Button>
      </section>
    </div>
  );
}

function KeyRow({ label, value, onChange, testid, secret, show, onToggle }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</Label>
      <div className="mt-2 flex items-center gap-2">
        <Input
          type={secret && !show ? "password" : "text"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 rounded-2xl font-mono text-xs"
          data-testid={testid}
        />
        {secret && onToggle && (
          <button type="button" onClick={onToggle} className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
        <button type="button" onClick={copy} className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500">
          <Copy className="w-4 h-4" />
        </button>
        {copied && <span className="text-[10px] text-emerald-600 font-bold">COPIED</span>}
      </div>
    </div>
  );
}

export function AdminBranding() {
  const { refresh } = usePlatform();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/platform-settings").then((r) => setS(r.data)); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/platform-settings", {
        title: s.title, tagline: s.tagline, description: s.description,
        logo_url: s.logo_url, favicon_url: s.favicon_url,
        meta_description: s.meta_description, meta_keywords: s.meta_keywords,
      });
      setS(data);
      await refresh();
      toast.success("Branding updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="text-slate-500">Loading…</div>;
  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-branding">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Look & feel</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight">Branding & SEO</h1>
      </div>
      <section className="rounded-3xl bg-white border border-slate-200 p-6 space-y-4">
        <Field label="Platform title" value={s.title} onChange={(v) => setS({ ...s, title: v })} testid="admin-branding-title" />
        <Field label="Tagline" value={s.tagline} onChange={(v) => setS({ ...s, tagline: v })} testid="admin-branding-tagline" />
        <Field label="Description" value={s.description} onChange={(v) => setS({ ...s, description: v })} multiline testid="admin-branding-description" />
        <Field label="Logo URL" value={s.logo_url} onChange={(v) => setS({ ...s, logo_url: v })} testid="admin-branding-logo" />
        {s.logo_url && (
          <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200 flex items-center gap-3">
            <img src={s.logo_url} alt="logo preview" className="w-10 h-10 rounded-xl object-contain" />
            <div className="text-xs text-slate-500">Live preview</div>
          </div>
        )}
        <Field label="Favicon URL" value={s.favicon_url} onChange={(v) => setS({ ...s, favicon_url: v })} testid="admin-branding-favicon" />
        <Field label="Meta description (SEO)" value={s.meta_description} onChange={(v) => setS({ ...s, meta_description: v })} multiline testid="admin-branding-meta-description" />
        <Field label="Meta keywords (SEO)" value={s.meta_keywords} onChange={(v) => setS({ ...s, meta_keywords: v })} testid="admin-branding-meta-keywords" />
        <Button disabled={busy} onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="admin-branding-save">
          <Save className="w-4 h-4 mr-1.5" /> Save branding
        </Button>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, multiline, testid }) {
  const Cmp = multiline ? Textarea : Input;
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</Label>
      <Cmp value={value || ""} onChange={(e) => onChange(e.target.value)} className={`mt-2 rounded-2xl ${multiline ? "" : "h-12"}`} data-testid={testid} />
    </div>
  );
}

export function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const loadLeads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const deleteLead = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = leads.filter(
    (l) =>
      l.name?.toLowerCase().includes(search.toLowerCase()) ||
      l.email?.toLowerCase().includes(search.toLowerCase()) ||
      l.group_type?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Lead Conversion</div>
          <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight">AI Chatbot Leads</h1>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 rounded-2xl"
          />
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Loading leads...</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-200 p-12 text-center text-slate-500">
          No captured leads found.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((l) => (
            <div key={l.id} className="p-5 rounded-3xl bg-white border border-slate-200/80 card-soft space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 font-bold grid place-items-center">
                    {l.name?.[0]?.toUpperCase() || "L"}
                  </div>
                  <div>
                    <div className="heading font-bold text-slate-900 text-sm">{l.name}</div>
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                      {l.group_type || "General"} Group
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => deleteLead(l.id)}
                  className="w-8 h-8 rounded-full hover:bg-rose-50 grid place-items-center text-slate-400 hover:text-rose-600 transition-colors"
                  title="Delete Lead"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="text-xs space-y-1.5 text-slate-600 pt-1">
                {l.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    <span>{l.email}</span>
                  </div>
                )}
                {l.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    <span>{l.phone}</span>
                  </div>
                )}
                {l.created_at && (
                  <div className="flex items-center gap-2 text-slate-400 text-[11px]">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

